"use client";

import { useRouter, usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { BsSearch } from "react-icons/bs";
import { MdClose } from "react-icons/md";

import { getLabel } from "@/data/labels";
import { getLocaleFromPathname, getLocalePrefix } from "@/utils/getLocaleFromPath";

interface SearchInputProps {
  query?: string;
  handleSearch?: () => void;
  lang?: string;
}

export default function SearchInput({ query, handleSearch, lang: langProp }: SearchInputProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname) || "te-IN";
  const lang = langProp ?? locale?.split("-")[0] ?? "te";
  const localePrefix = getLocalePrefix(pathname);

  const router = useRouter();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (searchQuery?.trim()) {
      const encoded = encodeURIComponent(searchQuery.trim());
      // Avoid /te-IN/search → middleware 301 to /search (can drop query on client nav).
      // Use visible locale prefix only when it is already in the URL (e.g. /en-IN).
      router.push(`${localePrefix}/search?q=${encoded}`);

      if (handleSearch) handleSearch();
    }
  };

  useEffect(() => {
    setSearchQuery(query || "");
  }, [query]);

  return (
    <form onSubmit={handleSubmit}>
      <div className="border border-[#D9D9D9] dark:border-[#7A7A7A] w-full max-w-[600px] h-[60px] rounded-xl mx-auto overflow-hidden flex items-center gap-4 pl-4">
        <span className="text-[18px] text-[#333] dark:text-white">
          <BsSearch />
        </span>
        <input
          type="text"
          className="w-full h-full bg-transparent outline-none text-[#1a1a1a] placeholder:text-[#6b7280] dark:text-white dark:placeholder:text-gray-400"
          placeholder={getLabel(lang, "SEARCH_PLACEHOLDER")}
          onChange={e => setSearchQuery(e.target.value)}
          onInvalid={e => e.currentTarget.setCustomValidity(getLabel(lang, "PLEASE_FILL_THIS_FIELD"))}
          onInput={e => e.currentTarget.setCustomValidity("")}
          value={searchQuery || ""}
          required
        />
        {searchQuery?.length ? (
          <span
            onClick={() => setSearchQuery("")}
            className="text-[18px] text-[#333] dark:text-white cursor-pointer"
          >
            <MdClose />
          </span>
        ) : (
          ""
        )}
        <div className="h-full p-1">
          <button
            type="submit"
            className="uppercase bg-primaryColor rounded-xl h-full text-white px-4 w-[120px] text-sm"
            aria-label={getLabel(lang, "SEARCH")}
          >
            {getLabel(lang, "SEARCH")}
          </button>
        </div>
      </div>
    </form>
  );
}
